import moment from 'moment/moment';
import { FormattedMessage } from 'react-intl';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  FIELD_AMOUNT,
  FIELD_DATE,
  FIELD_GROUP_BUTTONS,
  FIELD_TEXT_REQUIRED,
  GridProps,
  LAYOFF_PERIOD_OPTIONS,
  SMALL_GRID_PROPS,
  JOB_FIELD_NAMES,
  EMPLOYMENT_TYPE,
  MESSAGE_IDS as BASE_MESSAGE_IDS,
  ADDRESS_FIELDS,
} from './constants.job';
import { PAY_FREQUENCY } from 'pages/AffordaWagesAndIncome/constants';
import { FIELD_MEMBERS } from 'pages/AffordaWagesAndIncome/constants.fields';
import { validateNoSpecialChars } from 'utils/validateNoSpecialChars';

export const MAX_RECORDS_BY_USER = 1;

export const MESSAGE_IDS = {
  ...BASE_MESSAGE_IDS,
  title: 'afforda.wagesAndIncome.details.anticipated.title',
  description: 'afforda.wagesAndIncome.details.anticipated.subtitle',
};

export const PAY_FREQUENCY_OPTIONS = {
  HOURLY: {
    value: PAY_FREQUENCY.hourly,
    text: 'afforda.wagesAndIncome.details.job.hourly',
  },
  ANNUALLY: {
    value: PAY_FREQUENCY.annually,
    text: 'afforda.wagesAndIncome.details.job.annually',
  },
};

const FIELD_SHOW_COMMON = {
  watchFields: [JOB_FIELD_NAMES.memberId],
  hidden: ({ getValues }) => !getValues(JOB_FIELD_NAMES.memberId)?.length,
};

const FIELD_SHOW_NO_MILITARY = {
  watchFields: [JOB_FIELD_NAMES.memberId, JOB_FIELD_NAMES.employmentType],
  hidden: ({ getValues }) =>
    !getValues(JOB_FIELD_NAMES.memberId)?.length ||
    !getValues(JOB_FIELD_NAMES.employmentType)?.length ||
    getValues(JOB_FIELD_NAMES.employmentType) === EMPLOYMENT_TYPE.military,
};

const FIELD_SHOW_NO_SELF_EMPLOYED = {
  watchFields: [JOB_FIELD_NAMES.memberId, JOB_FIELD_NAMES.employmentType],
  hidden: ({ getValues }) =>
    !getValues(JOB_FIELD_NAMES.memberId)?.length ||
    !getValues(JOB_FIELD_NAMES.employmentType)?.length ||
    getValues(JOB_FIELD_NAMES.employmentType) === EMPLOYMENT_TYPE.selfEmployed,
};

const FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED = {
  watchFields: [JOB_FIELD_NAMES.memberId, JOB_FIELD_NAMES.employmentType],
  hidden: ({ getValues }) =>
    !getValues(JOB_FIELD_NAMES.memberId)?.length ||
    !getValues(JOB_FIELD_NAMES.employmentType)?.length ||
    getValues(JOB_FIELD_NAMES.employmentType) ===
      EMPLOYMENT_TYPE.selfEmployed ||
    getValues(JOB_FIELD_NAMES.employmentType) === EMPLOYMENT_TYPE.military,
};

const FIELD_SHOW_SEASONAL = {
  watchFields: [JOB_FIELD_NAMES.memberId, JOB_FIELD_NAMES.employmentType],
  hidden: ({ getValues }) =>
    !getValues(JOB_FIELD_NAMES.memberId)?.length ||
    getValues(JOB_FIELD_NAMES.employmentType) !== EMPLOYMENT_TYPE.seasonal,
};

const FIELD_SHOW_PAY_HOURLY_SEMI_OR_BIWEEKLY = {
  watchFields: [JOB_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    getValues(JOB_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.hourly &&
    getValues(JOB_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.biweekly &&
    getValues(JOB_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.semimonthly,
};

const FIELD_SHOW_PAY_MONTHLY = {
  watchFields: [JOB_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    getValues(JOB_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.monthly,
};

const FIELD_SHOW_PAY_WEEKLY = {
  watchFields: [JOB_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    getValues(JOB_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.weekly,
};

const FIELD_SHOW_PAY_ANNUALLY = {
  watchFields: [JOB_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    getValues(JOB_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.annually,
};

const KEYS_ANTICIPATED_EMPLOYMENT_TYPES = [
  'FULL_TIME',
  'PART_TIME',
  'SEASONAL',
];

const ANTICIPATED_EMPLOYMENT_TYPE_OPTIONS = Object.fromEntries(
  Object.entries(EMPLOYMENT_TYPE_OPTIONS).filter(([key]) =>
    KEYS_ANTICIPATED_EMPLOYMENT_TYPES.includes(key),
  ),
);

export const FIELDS = ({
  disabledMembers,
  states,
  intl,
  members,
  validateZip,
}) => {
  return [
    ...FIELD_MEMBERS({ members, disabledMembers }),
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_COMMON,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.employmentType" />
      ),
      name: JOB_FIELD_NAMES.employmentType,
      options: Object.values(ANTICIPATED_EMPLOYMENT_TYPE_OPTIONS)?.map(
        (option) => ({
          value: option.value,
          label: <FormattedMessage id={option.text} />,
        }),
      ),
    },
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.position" />
      ),
      name: JOB_FIELD_NAMES.position,
      rules: {
        required: true,
        validate: validateNoSpecialChars,
      },
      GridProps,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.position',
      }),
    },
    // Start Date - No Military, No Self Employed
    {
      ...FIELD_DATE,
      ...FIELD_SHOW_NO_MILITARY_NO_SELFEMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.anticipated.startDate" />
      ),
      name: JOB_FIELD_NAMES.startDate,
      disableFuture: false,
      disablePast: false,
      defaultCalendarMonth: moment(moment().month(), 'mm'),
      rules: {
        minDate: moment().startOf('day'),
        required: true,
      },
      GridProps: SMALL_GRID_PROPS,
    },
    // Common - Company
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.company.name" />
      ),
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.company.name',
      }),
      name: JOB_FIELD_NAMES.employerName,
      rules: {
        validate: validateNoSpecialChars,
      },
    },
    ...ADDRESS_FIELDS({ intl, states, validateZip }),
    //
    // Supervisor - No Self Employed
    //
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_SELF_EMPLOYED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.supervisor.name" />
      ),
      name: JOB_FIELD_NAMES.supervisorName,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.supervisor.name',
      }),
    },
    {
      ...FIELD_TEXT_REQUIRED,
      ...FIELD_SHOW_NO_SELF_EMPLOYED,
      variant: 'phone',
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.supervisor.phone" />
      ),
      name: JOB_FIELD_NAMES.supervisorPhone,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.supervisor.phone',
      }),
      rules: {
        validate: validateNoSpecialChars,
      },
    },
    //
    // Employment Type - Seasonal
    //
    {
      ...FIELD_SHOW_SEASONAL,
      variant: 'date',
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.doNotwork" />
      ),
      name: JOB_FIELD_NAMES.doNotWorkStartDate,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.start',
      }),
      defaultCalendarMonth: moment(moment().year(), 'YYYY'),
      rules: {
        minDate: moment(moment().year(), 'YYYY'),
        maxDate: moment(moment().year(), 'YYYY').add(1, 'year'),
        required: true,
      },
      GridProps: SMALL_GRID_PROPS,
      helperText: null,
    },
    {
      ...FIELD_SHOW_SEASONAL,
      variant: 'date',
      name: JOB_FIELD_NAMES.doNotWorkEndDate,
      placeholder: intl?.formatMessage?.({
        id: 'afforda.wagesAndIncome.details.job.end',
      }),
      defaultCalendarMonth: moment(moment().year(), 'YYYY'),
      rules: {
        minDate: moment(moment().year(), 'YYYY'),
        maxDate: moment(moment().year(), 'YYYY').add(1, 'year'),
        required: true,
      },
      GridProps: SMALL_GRID_PROPS,
      sx: {
        paddingTop: { xs: 0, sm: '24px' },
      },
    },

    // Common - Pay Frequency (No Military)
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_SHOW_NO_MILITARY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency" />
      ),
      name: JOB_FIELD_NAMES.payFrequency,
      rules: {
        required: true,
      },
      options: Object.values(PAY_FREQUENCY_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
    },

    //
    // Pay Frequency - Hourly
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_HOURLY_SEMI_OR_BIWEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.hourly" />
      ),
      name: JOB_FIELD_NAMES.hourlyPay,
    },
    {
      ...FIELD_SHOW_PAY_HOURLY_SEMI_OR_BIWEEKLY,
      variant: 'number',
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.avgHours" />
      ),
      name: JOB_FIELD_NAMES.hoursPerWeek,
      rules: {
        required: true,
      },
      GridProps,
    },

    // ----------------
    // Pay Frequency - Weekly
    // ----------------
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_WEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.weekly" />
      ),
      name: JOB_FIELD_NAMES.weeklyPay,
    },
    // ----------------
    // Pay Frequency - Monthly
    // ----------------
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_MONTHLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.monthly" />
      ),
      name: JOB_FIELD_NAMES.monthlyPay,
    },

    // ----------------
    // Pay Frequency - Annually
    // ----------------
    {
      ...FIELD_AMOUNT,
      ...FIELD_SHOW_PAY_ANNUALLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.payFrequency.annual" />
      ),
      name: JOB_FIELD_NAMES.annualPay,
    },

    // ----------------
    // Layoff Period
    // ----------------
    {
      ...FIELD_SHOW_SEASONAL,
      variant: 'buttonGroup',
      multiple: true,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.layoffPeriod" />
      ),
      name: JOB_FIELD_NAMES.layoffPeriod,
      GridProps: {
        xs: 12,
        sm: 12,
      },
      rules: {
        required: true,
      },
      helperText: (
        <FormattedMessage id="afforda.wagesAndIncome.details.job.layoffPeriod.selectAll" />
      ),
      direction: 'column',
      options: Object.values(LAYOFF_PERIOD_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
    },
  ];
};

export const ANTICIPATED_JOB_FIELD_NAMES = FIELDS({}).reduce((acc, field) => {
  acc[field.name] = field.name;
  return acc;
}, {});
